/**
 * Expédition — mapping formulaire profil colis → POST/PUT /expedition/profiles.
 * Whitelist explicite (évite name absent/vide au submit).
 */

export const EXPEDITION_PROFILE_FORM_FIELD_KEYS = Object.freeze([
  'name',
  'description',
  'weightGrams',
  'lengthMm',
  'widthMm',
  'heightMm',
]);

export function createEmptyExpeditionProfileForm() {
  return {
    name: '',
    description: '',
    weightGrams: 500,
    lengthMm: 200,
    widthMm: 150,
    heightMm: 100,
  };
}

/**
 * @param {Record<string, unknown>} [raw]
 */
export function normalizeExpeditionProfileForForm(raw = {}) {
  const source = raw && typeof raw === 'object' ? raw : {};
  return {
    name: source.name == null ? '' : String(source.name),
    description: source.description == null ? '' : String(source.description),
    weightGrams: source.weightGrams == null ? 500 : source.weightGrams,
    lengthMm: source.lengthMm == null ? 200 : source.lengthMm,
    widthMm: source.widthMm == null ? 150 : source.widthMm,
    heightMm: source.heightMm == null ? 100 : source.heightMm,
  };
}

function toPositiveInt(value, fieldLabel) {
  const n = Number(value);
  if (!Number.isInteger(n) || n <= 0) {
    throw new Error(`${fieldLabel} doit être un entier strictement positif.`);
  }
  return n;
}

/**
 * @param {Record<string, unknown>} form
 * @param {string} commercantId
 * @param {{ nameOverride?: string }} [options] — secours DOM (autofill sans onChange React)
 */
export function buildExpeditionProfileCreatePayload(form, commercantId, options = {}) {
  const normalized = normalizeExpeditionProfileForForm(form);
  const trimmedName = String(options.nameOverride ?? normalized.name).trim();
  if (!trimmedName) {
    throw new Error('Le nom du profil est obligatoire.');
  }

  const description = String(normalized.description).trim();

  return {
    commercantId: String(commercantId || ''),
    name: trimmedName,
    description: description === '' ? null : description,
    weightGrams: toPositiveInt(normalized.weightGrams, 'Poids'),
    lengthMm: toPositiveInt(normalized.lengthMm, 'Longueur'),
    widthMm: toPositiveInt(normalized.widthMm, 'Largeur'),
    heightMm: toPositiveInt(normalized.heightMm, 'Hauteur'),
    isActive: true,
  };
}

/**
 * @param {Record<string, unknown>} form
 * @param {{ nameOverride?: string }} [options]
 */
/**
 * Même logique que ExpeditionManager.resolveProfileNameForSubmit (state puis DOM).
 * @param {unknown} profileFormName
 * @param {unknown} domInputValue
 */
export function resolveExpeditionProfileNameForSubmit(profileFormName, domInputValue) {
  const fromState = String(profileFormName ?? '').trim();
  if (fromState) return fromState;
  return String(domInputValue ?? '').trim();
}

/**
 * Chemin submit création profil (state + secours DOM → POST body).
 * @param {Record<string, unknown>} profileForm
 * @param {string} commercantId
 * @param {unknown} domInputValue
 */
export function buildExpeditionProfileCreatePayloadFromSubmit(
  profileForm,
  commercantId,
  domInputValue,
) {
  const resolvedName = resolveExpeditionProfileNameForSubmit(profileForm?.name, domInputValue);
  const nameOpts = resolvedName ? { nameOverride: resolvedName } : {};
  return buildExpeditionProfileCreatePayload(profileForm, commercantId, nameOpts);
}

export function buildExpeditionProfileUpdatePayload(form, options = {}) {
  const normalized = normalizeExpeditionProfileForForm(form);
  const trimmedName = String(options.nameOverride ?? normalized.name).trim();
  if (!trimmedName) {
    throw new Error('Le nom du profil est obligatoire.');
  }

  const description = String(normalized.description).trim();

  return {
    name: trimmedName,
    description: description === '' ? null : description,
    weightGrams: toPositiveInt(normalized.weightGrams, 'Poids'),
    lengthMm: toPositiveInt(normalized.lengthMm, 'Longueur'),
    widthMm: toPositiveInt(normalized.widthMm, 'Largeur'),
    heightMm: toPositiveInt(normalized.heightMm, 'Hauteur'),
  };
}
